import axios from "axios";
import {
  getBaseUrl,
  Microservice,
} from "@funded-here-interface/common/src/constant/baseUrl";

/**
 * Interface for Wallex Screening Status Response
 */
interface WallexStatusResponse {
  screeningStatus?: string;
  uploadStatus?: string;
  screeningRemarks?: string;
  registrationDate?: string;
  passportPhotos?: Record<
    string,
    { ownerId: string; data: string; passportUserId?: string }
  >;
  uploadedDocuments?: string[];
  failedUploads?: number;
  failedDocuments?: Array<{ fileName: string; errorMessage?: string }>;
  documentTimestamps?: Record<string, string>;
  results?: Array<{ status: string; fileName: string }>;
  excludedFiles?: string[];
}

/**
 * Interface for Wallex User Information Payload
 */
export interface WallexUserInfoPayload {
  userIdFH: string;
  accountType: string;
  email: string;
  firstName: string;
  lastName: string;
  countryCode: string;
  companyName: string;
  gender: string;
  mobileCountryCode: string;
  mobileNumber: number;
  employmentStatus: string;
  employmentIndustry: string;
  employmentPosition: string;
  dateOfBirth: string;
  countryOfBirth: string;
  residentialAddress: string;
  postalCode: string;
  nationality: string;
  identificationType: string;
  identificationNumber: string;
  issueDate: string;
  expiryDate: string;
  registrationNumber: string;
  businessType: string;
  incorporationDate: string;
  city: string;
  state: string;
  companyAddress: string;
  companyPostalCode: string;
  customerBusinessWebsite: string;
  customerBusinessDescription: string;
  entityType: string;
  ownershipStructureLayers: string;
  countryOfIncorporation: string;
  countryOfOperations: string;
  paymentModes: string;
  onboardingMode: string;
  productServiceComplexity: string;
  isPermanentPlaceOfBusiness: string;
  siteVisitPerformedAtAddress: string;
  isPremisesInLineWithBusiness: string;
  isHigherRiskEntityType: string;
  higherRiskEntityType: string;
  productUsage: string;
  purposeOfAccountOpening: string;
  isMakingPaymentsToIndividuals: string;
  makingPaymentsToIndividualsDescription: string;
  isMakingThirdPartyPayments: string;
  sourceOfFunds: string;
  primaryTransactingCountriesInbound: string;
  primaryTransactingCountriesOutbound: string;
  topCounterPartiesInbound: string;
  topCounterPartiesOutbound: string;
  anticipatedVolumeMonthlyTransactionsCountInbound: number;
  anticipatedVolumeMonthlyTransactionsCountOutbound: number;
  anticipatedValueMonthlyTransactionsInbound: number;
  anticipatedValueMonthlyTransactionsOutbound: number;
  currencyPairsInbound: string;
  currencyPairsOutbound: string;
  excludedFiles: string[];
}

/**
 * Interface for Wallex Document Item
 */
interface WallexDocumentItem {
  fileName: string;
  documentType: string;
  contentType: string;
  path?: string;
  userId?: string | number;
  passportUserId?: string | number;
  fileBuffer?: string;
}

export const checkWallexStatus = async (
  userId: string | number,
  token: string,
  includePhotos: boolean = false
): Promise<WallexStatusResponse> => {
  try {
    const url = `${getBaseUrl(Microservice.TRADE_DIRECTORY)}/trade-directory/wallex-onboarding/check-status/${userId}?t=${Date.now()}${
      includePhotos ? "&include_photos=true" : ""
    }`;

    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error checking Wallex status:", error);
    throw error;
  }
};

export const wallexInfoUpload = async (
  payload: WallexUserInfoPayload,
  token: string
): Promise<Record<string, unknown>> => {
  try {
    const apiEndpoint = `${getBaseUrl(Microservice.TRADE_DIRECTORY)}/trade-directory/wallex-onboarding/upload`;

    const response = await axios.post(apiEndpoint, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error uploading onboarding info to Wallex:", error);
    throw error;
  }
};

export const uploadWallexDocuments = async (
  documents: WallexDocumentItem[],
  token: string
): Promise<Record<string, unknown>> => {
  try {
    if (documents.length === 0) {
      return { message: "No documents to upload" };
    }

    const apiEndpoint = `${getBaseUrl(Microservice.TRADE_DIRECTORY)}/trade-directory/wallex-onboarding/uploaddoc`;

    const response = await axios.post(apiEndpoint, documents, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error uploading documents to Wallex:", error);
    throw error;
  }
};
